/**
 * AP-32 8.5 — **Der Mahnvorschlag.** Offene, fällige Rechnungen je Empfänger
 * und Währung, mit Stufe, Gebühr und Verzugszinsen. Aus dem Vorschlag entstehen
 * **Entwürfe** — versendet wird nichts automatisch, und die Stufe an der
 * Rechnung setzt erst das Festschreiben der Mahnung.
 */

import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import { ArrowLeft, BellRing, RefreshCw } from 'lucide-react'
import { fehlertext } from '@/lib/fehlertext'
import { pageWide } from '@/lib/form-styles'
import { documentsApi } from '../api/documents.api'
import { formatBetrag, formatDatum } from '../lib/grid'
import type { DunningProposal } from '../types/document.types'

const schluessel = (g: DunningProposal) => `${g.clientId ?? g.partnerName}|${g.currency}`

export default function DunningPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [vorschlag, setVorschlag] = useState<DunningProposal[] | null>(null)
  const [auswahl, setAuswahl] = useState<Record<string, Set<string>>>({})
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)

  const laden = useCallback(async () => {
    setFehler(null)
    try {
      const { data } = await documentsApi.dunningProposal()
      setVorschlag(data.data)
      setAuswahl(Object.fromEntries(data.data.map((g) => [schluessel(g), new Set(g.invoices.map((i) => i.id))])))
    } catch (err) {
      setFehler(fehlertext(err, t))
      setVorschlag([])
    }
  }, [t])

  useEffect(() => {
    void laden()
  }, [laden])

  const umschalten = (g: DunningProposal, id: string) =>
    setAuswahl((a) => {
      const neu = new Set(a[schluessel(g)])
      if (neu.has(id)) neu.delete(id)
      else neu.add(id)
      return { ...a, [schluessel(g)]: neu }
    })

  const anlegen = async (g: DunningProposal) => {
    const ids = [...(auswahl[schluessel(g)] ?? [])]
    if (!ids.length) return
    setLaeuft(true)
    setFehler(null)
    try {
      const { data } = await documentsApi.createDunning(ids)
      navigate(`/correspondence/${data.data.id}`)
    } catch (err) {
      setFehler(fehlertext(err, t))
    } finally {
      setLaeuft(false)
    }
  }

  return (
    <div className={pageWide}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <Link to="/correspondence" className="text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-foreground">{t('documents.dunning.title')}</h1>
            <p className="mt-1 text-sm text-muted-foreground">{t('documents.dunning.subtitle')}</p>
          </div>
        </div>
        <button type="button" onClick={() => void laden()} className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-2 text-sm hover:bg-card">
          <RefreshCw className="h-4 w-4" />
          {t('documents.dunning.refresh')}
        </button>
      </div>

      {fehler && <div className="rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">{fehler}</div>}
      {vorschlag === null && <p className="text-sm text-muted-foreground">{t('common.loading')}</p>}
      {vorschlag?.length === 0 && !fehler && <p className="rounded-lg border border-dashed border-border px-4 py-8 text-center text-sm text-muted-foreground">{t('documents.dunning.empty')}</p>}

      {vorschlag?.map((g) => {
        const gewaehlt = auswahl[schluessel(g)] ?? new Set<string>()
        return (
          <section key={schluessel(g)} className="rounded-lg border border-border bg-card shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3">
              <div>
                <p className="font-semibold">{g.partnerName}</p>
                <p className="text-xs text-muted-foreground">
                  {t('documents.dunning.level', { level: g.level })} · {t('documents.dunning.fee')} {formatBetrag(g.fee, g.currency)} · {t('documents.dunning.interest')}{' '}
                  {formatBetrag(g.interest, g.currency)} · {t('documents.dunning.total')} <span className="font-medium text-foreground">{formatBetrag(g.total, g.currency)}</span>
                </p>
              </div>
              <button
                type="button"
                disabled={laeuft || gewaehlt.size === 0}
                onClick={() => void anlegen(g)}
                className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
              >
                <BellRing className="h-4 w-4" />
                {t('documents.dunning.create', { level: g.level })}
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-border bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="w-8 px-3 py-2" />
                    <th className="px-3 py-2">{t('documents.fields.number')}</th>
                    <th className="px-3 py-2">{t('documents.fields.documentDate')}</th>
                    <th className="px-3 py-2">{t('documents.fields.dueDate')}</th>
                    <th className="px-3 py-2 text-right">{t('documents.dunning.daysOverdue')}</th>
                    <th className="px-3 py-2 text-right">{t('documents.fields.grossTotal')}</th>
                    <th className="px-3 py-2 text-right">{t('documents.fields.openAmount')}</th>
                    <th className="px-3 py-2 text-right">{t('documents.dunning.nextLevel')}</th>
                  </tr>
                </thead>
                <tbody>
                  {g.invoices.map((i, n) => (
                    <tr key={i.id} className={`border-b border-border last:border-0 ${n % 2 ? 'bg-muted/20' : 'bg-background'}`}>
                      <td className="px-3 py-1.5">
                        <input type="checkbox" checked={gewaehlt.has(i.id)} onChange={() => umschalten(g, i.id)} />
                      </td>
                      <td className="px-3 py-1.5">
                        <Link to={`/correspondence/${i.id}`} className="font-mono text-primary hover:underline">
                          {i.documentNumber}
                        </Link>
                      </td>
                      <td className="px-3 py-1.5 tabular-nums">{formatDatum(i.documentDate)}</td>
                      <td className="px-3 py-1.5 tabular-nums">{formatDatum(i.dueDate)}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{i.daysOverdue}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{formatBetrag(i.grossTotal, g.currency)}</td>
                      <td className="px-3 py-1.5 text-right font-medium tabular-nums">{formatBetrag(i.openAmount, g.currency)}</td>
                      <td className="px-3 py-1.5 text-right">{i.level}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )
      })}
    </div>
  )
}
